// src/api/http.js
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from "../config";
import { getToken } from "../utils/auth";
import { authService } from "./authService";

// Endpoints where a 401 means "bad credentials", not "expired token".
// Retrying these would burn a second failed login attempt per click and trip
// the backend account lockout in half the clicks.
const NO_REFRESH_RETRY_PATHS = [API_ENDPOINTS.LOGIN, API_ENDPOINTS.REGISTER];

// Re-export for backwards compatibility
export const API_BASE = API_BASE_URL;

export const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// State for token refresh queue
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * @param {Function} callback - Called with new token when refresh completes
 */
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that token has been refreshed
 * @param {string} token - New access token
 */
function onTokenRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

/**
 * API fetch wrapper with automatic token refresh on 401 responses
 *
 * @param {string} path - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const apiFetch = async (path, options = {}) => {
  const { headers, _retry, ...rest } = options;

  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);

  const config = {
    ...rest,
    headers: {
      ...(headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  let response = await fetch(`${API_BASE_URL}${path}`, config);

  // If 402 Payment Required, redirect to pricing
  if (response.status === 402) {
    // Save current URL for redirect after payment
    sessionStorage.setItem("redirectAfterPayment", window.location.pathname);
    window.location.href = "/pricing";
    throw new Error("Subscription required");
  }

  // If unauthorized and not already a retry, attempt token refresh.
  // Credential endpoints are excluded: a 401 there is a wrong password, and
  // retrying it would count as a second failed login attempt.
  const skipRefreshRetry = NO_REFRESH_RETRY_PATHS.some((p) => path.startsWith(p));

  if (response.status === 401 && !_retry && !skipRefreshRetry) {
    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const newToken = await authService.refreshToken();
        isRefreshing = false;
        onTokenRefreshed(newToken);
      } catch (error) {
        isRefreshing = false;
        refreshSubscribers = [];

        // Redirect to login on refresh failure
        window.location.href = "/login";
        throw error;
      }
    }

    // Wait for the token refresh and retry the request
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((newToken) => {
        // Update authorization header with new token
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${newToken}`,
        };

        // Mark as retry to prevent infinite loops
        fetch(`${API_BASE_URL}${path}`, { ...config, _retry: true })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  return response;
};
